import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Building2, Plus, ToggleLeft, ToggleRight, Trash2, UserPlus, Edit2, Save, X, Users } from 'lucide-react';
import { API } from '../../utils/apiConfig';
import { B2BSmartAssignForm } from './B2BSmartAssignForm';
import { B2BMembersModal } from './B2BMembersModal';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

export const AdminB2BOrgsTab = () => {
  const { t } = useTranslation();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [createForm, setCreateForm] = useState({
    org_id: '', name: '', org_type: 'other', contact_email: '',
    contact_phone: '', secret_code: '', is_safety_enabled: false, allowed_domains: '',
  });
  const [showAssign, setShowAssign] = useState(false);
  const [membersModal, setMembersModal] = useState(null);
  const [message, setMessage] = useState(null);

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/b2b-orgs`, { headers: getHeaders() });
      setOrgs(res.data.organizations || []);
    } catch {
      setMessage({ type: 'error', text: t('errors.generic') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  const flash = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.org_id || !createForm.name) return flash('error', t('errors.validation'));
    try {
      await axios.post(`${API}/admin/b2b-orgs`, {
        ...createForm,
        allowed_domains: createForm.allowed_domains,
      }, { headers: getHeaders() });
      flash('success', t('b2b.orgCreated'));
      setShowCreate(false);
      setCreateForm({ org_id: '', name: '', org_type: 'other', contact_email: '', contact_phone: '', secret_code: '', is_safety_enabled: false, allowed_domains: '' });
      loadOrgs();
    } catch (err) {
      flash('error', err.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleToggleSafety = async (org) => {
    try {
      await axios.put(`${API}/admin/b2b-orgs/${org.id}`, { is_safety_enabled: !org.is_safety_enabled }, { headers: getHeaders() });
      flash('success', t('b2b.safetyToggled', { state: !org.is_safety_enabled ? t('b2b.enabled') : t('b2b.disabled'), name: org.name }));
      loadOrgs();
    } catch { flash('error', t('errors.generic')); }
  };

  const handleDelete = async (org) => {
    if (!window.confirm(t('b2b.confirmDelete', { name: org.name }))) return;
    try {
      await axios.delete(`${API}/admin/b2b-orgs/${org.id}`, { headers: getHeaders() });
      flash('success', t('b2b.orgDeleted'));
      loadOrgs();
    } catch { flash('error', t('errors.generic')); }
  };

  const startEdit = (org) => {
    setEditingId(org.id);
    setEditForm({
      org_id: org.org_id, name: org.name, org_type: org.org_type,
      contact_email: org.contact_email || '', contact_phone: org.contact_phone || '',
      secret_code: org.secret_code || '',
      allowed_domains: (org.allowed_domains || []).join(', '),
    });
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/admin/b2b-orgs/${editingId}`, {
        ...editForm,
        allowed_domains: editForm.allowed_domains,
      }, { headers: getHeaders() });
      flash('success', t('b2b.orgUpdated'));
      setEditingId(null);
      loadOrgs();
    } catch (err) { flash('error', err.response?.data?.detail || t('errors.generic')); }
  };

  const orgTypes = [
    { value: 'nursing_home', label: t('b2b.types.nursing_home') },
    { value: 'school', label: t('b2b.types.school') },
    { value: 'courier_fleet', label: t('b2b.types.courier_fleet') },
    { value: 'hospital', label: t('b2b.types.hospital') },
    { value: 'corporate', label: t('b2b.types.corporate') },
    { value: 'other', label: t('b2b.types.other') },
  ];

  const inputStyle = { width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid rgba(148,163,184,0.3)', background: 'rgba(15,23,42,0.6)', color: '#e2e8f0' };
  const labelStyle = { fontSize: '12px', color: '#94a3b8', display: 'block', marginBottom: '4px' };

  if (loading) return <div className="admin-tab-content" data-testid="b2b-orgs-loading"><div className="spinner" /></div>;

  return (
    <div className="admin-tab-content" data-testid="admin-b2b-orgs-tab">
      {message && (
        <div data-testid="b2b-message" style={{
          padding: '10px 16px', marginBottom: '16px', borderRadius: '8px',
          background: message.type === 'success' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
          color: message.type === 'success' ? '#22c55e' : '#ef4444',
          border: `1px solid ${message.type === 'success' ? '#22c55e' : '#ef4444'}`,
        }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ float: 'right', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}><X size={16} /></button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button className="admin-action-btn stats" onClick={() => setShowCreate(!showCreate)} data-testid="b2b-create-btn">
          <Plus size={16} /> {t('b2b.createOrg')}
        </button>
        <button className="admin-action-btn maintenance" onClick={() => setShowAssign(!showAssign)} data-testid="b2b-assign-btn">
          <UserPlus size={16} /> {t('b2b.assignUser')}
        </button>
      </div>

      {/* CREATE FORM */}
      {showCreate && (
        <form onSubmit={handleCreate} data-testid="b2b-create-form" style={{
          background: 'rgba(30,41,59,0.85)', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: '1px solid rgba(99,102,241,0.3)',
        }}>
          <h3 style={{ marginBottom: '16px', color: '#a5b4fc' }}><Building2 size={18} style={{ marginRight: '8px' }} />{t('b2b.createOrg')}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
            <div>
              <label style={labelStyle}>{t('b2b.orgId')} *</label>
              <input data-testid="b2b-create-org-id" value={createForm.org_id} onChange={e => setCreateForm({ ...createForm, org_id: e.target.value })} placeholder="e.g. nursing-home-berlin" required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>{t('b2b.orgName')} *</label>
              <input data-testid="b2b-create-name" value={createForm.name} onChange={e => setCreateForm({ ...createForm, name: e.target.value })} placeholder="Seniorenheim Berlin" required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>{t('b2b.orgType')}</label>
              <select data-testid="b2b-create-type" value={createForm.org_type} onChange={e => setCreateForm({ ...createForm, org_type: e.target.value })} style={inputStyle}>
                {orgTypes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>{t('b2b.contactEmail')}</label>
              <input data-testid="b2b-create-email" value={createForm.contact_email} onChange={e => setCreateForm({ ...createForm, contact_email: e.target.value })} placeholder="admin@org.de" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>{t('b2b.secretCode')}</label>
              <input data-testid="b2b-create-secret" value={createForm.secret_code} onChange={e => setCreateForm({ ...createForm, secret_code: e.target.value })} placeholder="safety-key-123" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>{t('b2b.allowedDomains')}</label>
              <input data-testid="b2b-create-domains" value={createForm.allowed_domains} onChange={e => setCreateForm({ ...createForm, allowed_domains: e.target.value })} placeholder={t('b2b.allowedDomainsHint')} style={inputStyle} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
              <label style={{ ...labelStyle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input type="checkbox" data-testid="b2b-create-safety-toggle" checked={createForm.is_safety_enabled} onChange={e => setCreateForm({ ...createForm, is_safety_enabled: e.target.checked })} />
                {t('b2b.enableSafetyDashboard')}
              </label>
            </div>
          </div>
          <div style={{ marginTop: '16px', display: 'flex', gap: '10px' }}>
            <button type="submit" className="admin-action-btn stats" data-testid="b2b-create-submit"><Save size={14} /> {t('common.save')}</button>
            <button type="button" className="admin-action-btn debug" onClick={() => setShowCreate(false)}><X size={14} /> {t('common.cancel')}</button>
          </div>
        </form>
      )}

      {/* SMART ASSIGN FORM */}
      {showAssign && <B2BSmartAssignForm orgs={orgs} onDone={() => { setShowAssign(false); loadOrgs(); }} flash={flash} />}

      {/* ORG TABLE */}
      <div style={{ overflowX: 'auto' }}>
        <table className="admin-table" data-testid="b2b-orgs-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th>{t('b2b.orgId')}</th>
              <th>{t('b2b.orgName')}</th>
              <th>{t('b2b.orgType')}</th>
              <th>{t('b2b.contactEmail')}</th>
              <th>{t('b2b.secretCode')}</th>
              <th>{t('b2b.allowedDomains')}</th>
              <th style={{ textAlign: 'center' }}>{t('b2b.safetyEnabled')}</th>
              <th>{t('b2b.members')}</th>
              <th>{t('admin.users.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {orgs.length === 0 ? (
              <tr><td colSpan={9} style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>{t('b2b.noOrgsYet')}</td></tr>
            ) : orgs.map(org => (
              <tr key={org.id} data-testid={`b2b-org-row-${org.org_id}`}>
                {editingId === org.id ? (
                  <>
                    <td><input value={editForm.org_id} onChange={e => setEditForm({ ...editForm, org_id: e.target.value })} data-testid="b2b-edit-org-id" style={{ ...inputStyle, width: '120px' }} /></td>
                    <td><input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} data-testid="b2b-edit-name" style={{ ...inputStyle, width: '140px' }} /></td>
                    <td>
                      <select value={editForm.org_type} onChange={e => setEditForm({ ...editForm, org_type: e.target.value })} data-testid="b2b-edit-type" style={{ ...inputStyle, width: '120px' }}>
                        {orgTypes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </td>
                    <td><input value={editForm.contact_email} onChange={e => setEditForm({ ...editForm, contact_email: e.target.value })} data-testid="b2b-edit-email" style={{ ...inputStyle, width: '140px' }} /></td>
                    <td><input value={editForm.secret_code} onChange={e => setEditForm({ ...editForm, secret_code: e.target.value })} data-testid="b2b-edit-secret" style={{ ...inputStyle, width: '100px' }} /></td>
                    <td><input value={editForm.allowed_domains} onChange={e => setEditForm({ ...editForm, allowed_domains: e.target.value })} data-testid="b2b-edit-domains" style={{ ...inputStyle, width: '150px' }} /></td>
                    <td />
                    <td />
                    <td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button className="admin-action-btn stats" onClick={handleSaveEdit} data-testid="b2b-edit-save" style={{ padding: '4px 10px', fontSize: '12px' }}><Save size={12} /></button>
                        <button className="admin-action-btn debug" onClick={() => setEditingId(null)} style={{ padding: '4px 10px', fontSize: '12px' }}><X size={12} /></button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{org.org_id}</td>
                    <td style={{ fontWeight: 600 }}>{org.name}</td>
                    <td><span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', background: 'rgba(99,102,241,0.15)', color: '#a5b4fc' }}>{t(`b2b.types.${org.org_type}`, org.org_type)}</span></td>
                    <td style={{ fontSize: '13px', color: '#94a3b8' }}>{org.contact_email || '—'}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#94a3b8' }}>{org.secret_code || '—'}</td>
                    <td style={{ fontSize: '12px', color: '#94a3b8' }}>{(org.allowed_domains || []).join(', ') || '—'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button onClick={() => handleToggleSafety(org)} data-testid={`b2b-safety-toggle-${org.org_id}`}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: org.is_safety_enabled ? '#22c55e' : '#64748b', transition: 'color 0.2s' }}
                        title={org.is_safety_enabled ? t('b2b.disabled') : t('b2b.enabled')}>
                        {org.is_safety_enabled ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                      </button>
                    </td>
                    <td>
                      <button className="admin-action-btn stats" onClick={() => setMembersModal(org)} data-testid={`b2b-members-${org.org_id}`}
                        style={{ padding: '4px 10px', fontSize: '12px' }}>
                        <Users size={12} /> {org.member_count || 0}
                      </button>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button className="admin-action-btn debug" onClick={() => startEdit(org)} data-testid={`b2b-edit-${org.org_id}`} style={{ padding: '4px 10px', fontSize: '12px' }}><Edit2 size={12} /></button>
                        <button className="admin-action-btn maintenance" onClick={() => handleDelete(org)} data-testid={`b2b-delete-${org.org_id}`} style={{ padding: '4px 10px', fontSize: '12px', color: '#ef4444' }}><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MEMBERS MODAL */}
      {membersModal && <B2BMembersModal org={membersModal} onClose={() => { setMembersModal(null); loadOrgs(); }} flash={flash} />}
    </div>
  );
};
